/**
 * @file shared/desktop/desktopShell.ts
 * @brief What the Verblizr desktop app (DESKTOP/, Electron) adds to the page.
 *
 * @details
 * The desktop app's preload exposes `window.verblizrDesktop`. In a normal
 * browser tab it is absent, and every caller falls back to the plain web
 * behaviour, so nothing here changes the webpanel in a browser.
 *
 * @author DES
 * @date 30-09-2026
 */

export type VerblizrDesktop = {
  version: number;
  platform: string;
  /** Call audio (Zoom/Teams/Meet) via getDisplayMedia; see capture/systemAudio.ts. */
  systemAudio: boolean;
  /**
   * v2+: Google sign-in in the user's real browser. Resolves to a Google ID
   * token; rejects with an Error whose message is "CANCELLED" if they back out.
   */
  signInWithGoogle?: () => Promise<string>;

  // ---- v3+: the floating session widget (DESKTOP/src/widget.js) ----------
  /** Session page: publish live state for the widget (null = no session). */
  publishSession?: (state: DesktopSession | null) => void;
  /** Session page: commands from the widget. Returns the unsubscribe. */
  onSessionCommand?: (cb: (cmd: DesktopCommand) => void) => () => void;
  /** Widget windows. */
  getSession?: () => Promise<DesktopSession | null>;
  onSession?: (cb: (state: DesktopSession | null) => void) => () => void;
  sendCommand?: (cmd: DesktopCommand) => void;
  restoreMain?: () => void;
  togglePanel?: (kind: DesktopPanel | null) => void;
  getPanel?: () => Promise<DesktopPanel | null>;
  onPanel?: (cb: (kind: DesktopPanel | null) => void) => () => void;
  /** Widget: is the pill window on screen (it always reads "visible" itself). */
  getWidgetVisible?: () => Promise<boolean>;
  onWidgetVisible?: (cb: (visible: boolean) => void) => () => void;
  reportHeight?: (height: number) => void;
  /** The widget pill's width (the one-row dock trial, 11-10-2026). Absent
   *  on older desktop builds, which keep their fixed width. */
  reportWidth?: (width: number) => void;
  /** Clicks pass through the widget window where it is empty. */
  setClickThrough?: (on: boolean) => void;
  /** Move the widget by hand (its logo tab). */
  dragWidget?: (phase: "start" | "move" | "end") => void;
  /** Main window: quick actions for the menu-bar icon (DesktopLauncherBridge). */
  setLaunchers?: (items: { id: string; label: string; path: string }[]) => void;
  /** Main window: a quick action was clicked; the payload is its path. */
  onLaunch?: (cb: (path: string) => void) => () => void;

  // ---- v4+: app menu + settings (DESKTOP/src/menu.js, settings.js) --------
  /** Main window: the app menu's Go items; clicks arrive on onLaunch. */
  setNavigation?: (items: { id: string; label: string; path: string }[]) => void;
  getSettings?: () => Promise<DesktopSettings | null>;
  onSettings?: (cb: (s: DesktopSettings) => void) => () => void;
  /** Settings window only. */
  setSettings?: (patch: Partial<DesktopSettings>) => Promise<DesktopSettings>;
  openSettings?: () => void;
  getAppInfo?: () => Promise<{ version: string; packaged: boolean; loginItems: boolean } | null>;
  /**
   * B43 (31-10-2026, ADR-061): SHA-256 of "verblizr-free-device:v1:" + the
   * Mac's IOPlatformUUID (hex), so free minutes are given once per Mac. The
   * raw id never leaves the main process; null = unreadable. Absent = an
   * older Mac build (the server then grants once per account only).
   */
  getFreeDeviceId?: () => Promise<string | null>;
  /** The main window's size (DESKTOP/src/windowState.js, 03-10-2026). */
  getWindowSize?: () => Promise<{ width: number; height: number } | null>;
  /** Main window back to 1280 × 860 (or 90% of a small screen), centred. */
  resetWindowSize?: () => Promise<{ width: number; height: number } | null>;

  // ---- v5+: Call mode's virtual mic (DESKTOP/src/callMic.js, 03-10-2026) --
  callMicStatus?: () => Promise<CallMicStatus>;
  /** One macOS administrator prompt; "CANCELLED" when the user closes it. */
  installCallMic?: () => Promise<{ ok: boolean; error?: string }>;
  /**
   * v6+: Verblizr Microphone as the Mac's default input (true), or the mic
   * before it back (false). On: `previous` names the mic it replaced.
   */
  setCallMicDefault?: (on: boolean) => Promise<{ ok: boolean; previous?: string; error?: string }>;
  /** v7+: call apps going on now (Zoom, Teams open; a browser in Meet using
   *  the mic…), by name; [] when none. Call mode is offered only then. */
  activeCallApps?: () => Promise<string[]>;

  // ---- v8+: the Mac's calendar (DESKTOP/src/calendar.js, 06-10-2026) ------
  /** "unsupported" on Windows (no shared calendar store). */
  calendarStatus?: () => Promise<DesktopCalendarStatus>;
  /** The macOS prompt, first time only; resolves the status after it. */
  requestCalendar?: () => Promise<DesktopCalendarStatus>;
  /** Events between two epoch-ms times, the phone module's shape. */
  calendarEvents?: (startMs: number, endMs: number) => Promise<DeviceCalendarEvent[]>;
  /** v9+: add / update one Verblizr event; its id, null = the user deleted it,
   *  undefined = failed (09-10-2026). */
  calendarSave?: (event: DesktopCalendarEventInput) => Promise<string | null | undefined>;
  /** v9+: delete one Verblizr event; false when already gone. */
  calendarRemove?: (eventId: string) => Promise<boolean>;
  /** v11+: the Mac's calendars, for "Choose calendars" (14-10-2026). */
  calendarList?: () => Promise<DeviceCalendar[]>;
  /** v12+: System Settings > Privacy & Security > Calendars, after Don't
   *  Allow (20-10-2026); true when it opened. */
  openCalendarSettings?: () => Promise<boolean>;

  // ---- v9+: scheduled alarms (DESKTOP/src/alarms.js, 09-10-2026) ----------
  /** The full list of this user's alarms ([] clears); resolves how many are set. */
  setAlarms?: (list: DesktopAlarm[]) => Promise<number>;

  // ---- v10+: native notifications (DESKTOP/src/notifications.js, 09-10-2026) --
  /** Sign-in hand-over so the main process can poll the notification feed;
   *  null on sign-out. Resolves true when accepted. */
  setNotificationAuth?: (auth: DesktopNotificationAuth | null) => Promise<boolean>;
  /** A notification was clicked: open this path. Returns the unsubscribe. */
  onNotificationOpen?: (cb: (path: string) => void) => () => void;

  // ---- v13+: B50 export, save / share a PDF (DESKTOP B50-D, 02-11-2026) ----
  /**
   * The Mac's Save dialog for a signed https PDF link, `fileName` as the
   * suggested name. `{ ok: false, error: "CANCELLED" }` when the user closes
   * it; any other error makes the page fall back to a plain download.
   */
  saveFile?: (file: DesktopFile) => Promise<DesktopFileResult>;
  /**
   * The Mac's Share menu (Mail, Messages, AirDrop…) for the same PDF, saved
   * under `fileName` in a temp folder first. Resolves when the menu closes.
   */
  shareFile?: (file: DesktopFile) => Promise<DesktopFileResult>;
};

/** B50: a PDF to save or share (`url` = a 1-hour signed link). */
export type DesktopFile = { url: string; fileName: string };
export type DesktopFileResult = { ok: boolean; path?: string; error?: "CANCELLED" | string };

/** One alarm the desktop rings at `fireMs`; a click opens `path`. */
export type DesktopAlarm = {
  id: string;
  fireMs: number;
  title: string;
  body: string;
  path: string;
};

export type DesktopCalendarEventInput = {
  eventId?: string | null;
  title: string;
  startMs: number;
  endMs: number;
  notes?: string;
  url?: string;
  /** Minutes BEFORE the start. */
  alarmMinutes?: number[];
};

/** What the desktop app needs to read GET /api/push/feed for this user. */
export type DesktopNotificationAuth = {
  token: string;
  userId: string;
  /** Absolute API origin (env.apiBaseUrl, else this page's origin). */
  apiBase: string;
  /** Where this user's pages live. */
  pathBase: "/app" | "/company";
};

export type DesktopCalendarStatus =
  | "notDetermined"
  | "denied"
  | "restricted"
  | "authorized"
  | "writeOnly"
  | "unsupported";

/** One event from the Mac's calendar (same shape as the phone's). */
export type DeviceCalendarParticipant = {
  name: string | null;
  email: string | null;
  status: string;
  type: "person" | "room" | "resource" | "group" | "unknown";
  isCurrentUser: boolean;
};

export type DeviceCalendarEvent = {
  id: string;
  title: string;
  startMs: number;
  endMs: number;
  allDay: boolean;
  cancelled: boolean;
  location: string | null;
  calendarTitle: string | null;
  /** v11+ (14-10-2026): which calendar, and notes / URL for the call-link check. */
  calendarId?: string | null;
  notes?: string | null;
  url?: string | null;
  sourceTitle: string | null;
  organizer: DeviceCalendarParticipant | null;
  attendees: DeviceCalendarParticipant[];
  selfDeclined?: boolean;
};

/** One calendar on the Mac (v11+). */
export type DeviceCalendar = {
  id: string;
  title: string;
  /** The account: "iCloud", "Google", "Exchange"… */
  sourceTitle: string | null;
  /** "#RRGGBB" */
  color: string | null;
};

/** "Verblizr Microphone" on this Mac (Interpreter Call mode). */
export type CallMicStatus = {
  installed: boolean;
  upToDate: boolean;
  /** This build of the app carries the driver. */
  available: boolean;
  installedVersion: string | null;
  bundledVersion: string | null;
};

/** The desktop app's own settings (⌘,), stored on the machine. */
export type DesktopSettings = {
  /** "" = system default. */
  micDeviceId: string;
  micLabel: string;
  /** Boardroom setup's "Include call audio" starts on. */
  callAudio: boolean;
  openAtLogin: boolean;
  showWidget: boolean;
  shortcut: boolean;
  autoUpdate: boolean;
};

export type DesktopPanel = "agenda" | "notes" | "end";

export type DesktopBookmark = { type: "decision" | "todo"; timestamp: string };

/**
 * What a live session tells the widget (02-10-2026). The timer runs in the
 * widget from `elapsedMs` + `at` while `running`, so the page publishes on
 * changes and every few seconds, not on every tick.
 */
export type DesktopSession = {
  kind: "boardroom" | "interpret";
  title: string;
  /** live = recording / listening; paused = held; busy = starting or ending. */
  status: "live" | "paused" | "busy";
  /** Interpreter: "Listening", "Translating"… */
  statusLabel?: string | null;
  elapsedMs: number;
  /** Date.now() when `elapsedMs` was read. */
  at: number;
  running: boolean;
  /** Boardroom */
  bookmarks?: DesktopBookmark[];
  agendaNoteIds?: string[];
  /** B49: the agenda check boxes (absent = none; older widgets ignore it). */
  agendaChecks?: {
    noteId: string;
    noteTitle: string;
    index: number;
    text: string;
    parentText?: string | null;
    checked: boolean;
    atSecs?: number | null;
  }[];
  /** Notes written or attached during the meeting (linked privately at the end). */
  meetingNoteIds?: string[];
  /** The note being written in the widget, if any. */
  writingNoteId?: string | null;
  /** Meeting Notes or full (ADR-030, 07-10-2026): Notes has no marks or
   *  agenda, and its notes button is "Take notes". Absent = full. */
  tier?: "notes" | "full";
  /** Meeting Notes: the title typed in Take notes so far (names the meeting
   *  when Setup gave none); the widget's title field starts from it. */
  notesTitle?: string;
  /** Interpreter */
  langs?: string;
  /** The pair as flags for the widget (05-10-2026): you, then the other person. */
  langFlags?: { code: string; locale?: string }[];
  caption?: { text: string; rtl: boolean } | null;
};

export type DesktopCommand =
  | { type: "toggle-hold" }
  | { type: "bookmark"; kind: "decision" | "todo" }
  /** B49: tick / untick an agenda item (key = `${noteId}:${index}`). */
  | { type: "agenda-tick"; key: string }
  | { type: "end" }
  /** Boardroom: throw the recording away (confirmed in the widget). */
  | { type: "discard" }
  | { type: "set-notes"; meetingNoteIds: string[]; writingNoteId: string | null }
  /** Meeting Notes: the Take notes title, typed in the widget (07-10-2026). */
  | { type: "set-title"; title: string };

export function desktopShell(): VerblizrDesktop | null {
  const shell = (window as unknown as { verblizrDesktop?: VerblizrDesktop }).verblizrDesktop;
  return shell && typeof shell === "object" ? shell : null;
}
